import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, LogIn, AlertCircle, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";
import { adminApi, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSelector from "@/components/LanguageSelector";
import { useI18n } from "@/lib/i18n";

export default function AdminLogin() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 2FA challenge state — shown after a valid password when 2FA is enabled.
  const [mfaTicket, setMfaTicket] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  const navigate = useNavigate();

  if (tokenStorage.get()) {
    return <Navigate to="/admin/leads" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.post(
        "/auth/login",
        { email, password },
        { withCredentials: true },
      );
      if (data.requires_2fa) {
        setMfaTicket(data.mfa_ticket);
        setMfaCode("");
        setError("");
        return;
      }
      tokenStorage.set(data.token);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(" · ")
            : "Login failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setMfaSubmitting(true);
    try {
      const { data } = await adminApi.post(
        "/auth/login/2fa",
        {
          mfa_ticket: mfaTicket,
          code: mfaCode.trim(),
          remember_device: rememberDevice,
        },
        { withCredentials: true },
      );
      tokenStorage.set(data.token);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(typeof detail === "string" ? detail : "Invalid authenticator code");
    } finally {
      setMfaSubmitting(false);
    }
  };

  const isMfaStep = Boolean(mfaTicket);

  return (
    <div
      data-testid="admin-login-page" data-a11y-scope="portal-shell"
      className="min-h-screen bg-[#04102B] text-white relative overflow-hidden flex items-start md:items-center justify-center px-4 sm:px-6 py-8 md:py-10"
    >
      <div className="pointer-events-none absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/12 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/8 blur-3xl" />

      {/* Global language selector — top-right, keyboard-focusable, never overlaps the card on any width. */}
      <div className="absolute top-4 right-4 sm:top-5 sm:right-6 z-10">
        <LanguageSelector testId="admin-login-lang" />
      </div>

      <form
        onSubmit={isMfaStep ? onSubmit2fa : onSubmit}
        data-testid={isMfaStep ? "admin-2fa-form" : "admin-login-form"}
        className="relative w-full max-w-md bg-[#0A1F44] rounded-3xl p-6 sm:p-8 md:p-10 border border-[#D4AF37]/25 shadow-2xl mt-14 md:mt-0"
      >
        {/* Centered brand lockup: logo + brand name pulled from the public site */}
        <div className="flex flex-col items-center text-center pb-2">
          <Logo size={112} testId="admin-login-logo" className="drop-shadow-[0_10px_40px_rgba(212,175,55,0.35)]" />
          <div className="mt-4 font-display text-white text-lg md:text-xl tracking-tight uppercase leading-tight">
            SAHYOG FINANCE
            <br />
            <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
          </div>
          <div className="font-display text-[#D4AF37] text-[10px] tracking-[0.28em] uppercase mt-1.5">
            Asset Management Pvt. Ltd.
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-white/40">
            Staff Portal
          </div>
        </div>

        {!isMfaStep && (
          <>
            <div className="mt-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
            <h1 className="mt-6 font-display text-2xl md:text-3xl text-center">{t("login.title")}</h1>
            <p className="text-white/60 text-xs text-center mt-1.5 tracking-wide">
              {t("login.subtitle.admin")}
            </p>

            <label className="block mt-7 text-[11px] tracking-[0.2em] uppercase text-white/55">
              {t("login.field.email")}
            </label>
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/25 text-white outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition"
            />

            <label className="block mt-5 text-[11px] tracking-[0.2em] uppercase text-white/55">
              {t("login.field.password")}
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/25 text-white outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition"
            />

            {error && (
              <div
                data-testid="admin-login-error"
                className="mt-5 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading || !password}
              className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
            >
              {loading ? (
                <>{t("login.submitting")}</>
              ) : (
                <>
                  <LogIn size={16} /> {t("login.submit")}
                </>
              )}
            </button>

            <div className="mt-4 text-center">
              <a
                href="/admin/forgot-password"
                data-testid="admin-forgot-link"
                className="text-xs text-[#F0D788] hover:underline"
              >
                {t("login.forgot")}
              </a>
            </div>

            <p className="mt-5 text-[11px] text-white/40 text-center inline-flex items-center justify-center gap-1.5 w-full">
              <Lock size={11} /> {t("login.secure")}
            </p>
          </>
        )}

        {isMfaStep && (
          <>
            <div className="mt-8 flex items-center gap-2.5 text-[#F0D788]">
              <ShieldCheck size={18} />
              <span className="text-xs tracking-[0.22em] uppercase">Two-Factor Authentication</span>
            </div>
            <h1 className="mt-2 font-display text-3xl">Verify your identity</h1>
            <p className="text-white/55 text-sm mt-1 leading-relaxed">
              Enter the 6-digit code from your authenticator app, or one of your one-time recovery codes.
            </p>

            <label className="block mt-6 text-[11px] tracking-[0.2em] uppercase text-white/55">
              Authenticator or recovery code
            </label>
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-white/[0.04] border border-[#D4AF37]/25 focus-within:border-[#D4AF37] px-4 py-3">
              <KeyRound size={16} className="text-white/40" />
              <input
                data-testid="admin-2fa-code-input"
                type="text"
                required
                autoFocus
                autoComplete="one-time-code"
                inputMode="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="123 456   or   abcd-efgh-ijkl"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/30 tracking-wide"
              />
            </div>

            <label className="mt-4 flex items-center gap-2.5 text-xs text-white/70 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="admin-2fa-remember-device"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              Remember this device for 30 days
            </label>

            {error && (
              <div
                data-testid="admin-2fa-error"
                className="mt-5 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <button
              data-testid="admin-2fa-submit"
              type="submit"
              disabled={mfaSubmitting || mfaCode.trim().length < 6}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
            >
              {mfaSubmitting ? <>Verifying…</> : <><ShieldCheck size={16} /> Verify &amp; sign in</>}
            </button>

            <button
              type="button"
              data-testid="admin-2fa-back"
              onClick={() => {
                setMfaTicket(null);
                setMfaCode("");
                setError("");
              }}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs text-white/55 hover:text-white"
            >
              <ArrowLeft size={12} /> Use a different account
            </button>
          </>
        )}
      </form>
    </div>
  );
}
